/**
 * Setting your own quick-switch PIN.
 *
 * Only ever your own — the API has no route for setting someone else's, on
 * purpose. A PIN a manager chose for you is a PIN the manager can use as you, and
 * the whole point of this feature is that attribution stays true.
 */
import { useState } from 'react';
import { isWeakPin } from '@inventory-tracker/core';
import { ApiError, clearPin, setPin } from '../lib/api.js';
import { c, field, label, sans } from '../theme.js';
import { PasswordInput } from './PasswordInput.js';

export function PinSettings({
  hasPin,
  onChanged,
}: {
  hasPin: boolean;
  onChanged: (message: string) => void;
}) {
  const [pin, setPinValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (pin !== confirm) {
      setError('Those PINs do not match');
      return;
    }
    // Same check the API runs. Catching it here saves a round trip; the server
    // still refuses independently, because a client check is a courtesy.
    if (isWeakPin(pin)) {
      setError('Avoid repeated digits or a simple run like 1234');
      return;
    }

    setBusy(true);
    try {
      await setPin(pin);
      setPinValue('');
      setConfirm('');
      onChanged('Your quick-switch PIN is set');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that PIN');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError('');
    try {
      await clearPin();
      onChanged('Your quick-switch PIN has been removed');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove your PIN');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 18,
        padding: '20px 22px',
        marginTop: 26,
      }}
    >
      <h3 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 20, margin: '0 0 4px' }}>
        Your quick-switch PIN
      </h3>
      <p style={{ fontSize: 12.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
        On a shared front-desk device, a PIN lets you take over without typing your password.
        It only works on a device someone has already signed into. It is never a way in on
        its own.
      </p>

      {hasPin && (
        <div
          style={{
            background: c.okBg,
            border: '1px solid #D3DFCE',
            color: c.success,
            borderRadius: 12,
            padding: '10px 13px',
            fontSize: 12.5,
            marginBottom: 14,
          }}
        >
          You have a PIN set. Entering a new one replaces it.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label htmlFor="pin" style={label}>
            {hasPin ? 'New PIN' : 'PIN'}
          </label>
          <PasswordInput
            what="PIN"
            id="pin"
            inputMode="numeric"
            autoComplete="off"
            pattern="\d{4,6}"
            maxLength={6}
            value={pin}
            onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
            placeholder="4 to 6 digits"
            required
            style={field}
          />
        </div>
        <div>
          <label htmlFor="pin-confirm" style={label}>
            Confirm
          </label>
          <PasswordInput
            what="PIN"
            id="pin-confirm"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}
            placeholder="Repeat it"
            required
            style={field}
          />
        </div>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            color: c.danger,
            borderRadius: 12,
            padding: '10px 13px',
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="submit"
          disabled={busy}
          style={{
            height: 44,
            padding: '0 20px',
            background: c.green,
            border: 'none',
            color: c.onDarkBright,
            fontSize: 14,
            fontWeight: 500,
            fontFamily: sans,
            borderRadius: 980,
            cursor: busy ? 'progress' : 'pointer',
          }}
        >
          {busy ? 'Saving…' : hasPin ? 'Replace my PIN' : 'Set my PIN'}
        </button>

        {hasPin && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            style={{
              background: 'transparent',
              border: `1px solid ${c.borderStrong}`,
              color: c.muted2,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: sans,
              padding: '11px 16px',
              borderRadius: 14,
              cursor: 'pointer',
            }}
          >
            Remove it
          </button>
        )}
      </div>
    </form>
  );
}
